/**
 * SlideshowControls custom element that adds thumbnail scroll button functionality
 * without interfering with the parent slideshow component's ref system.
 */
class SlideshowControls extends HTMLElement {
  /** @type {() => void} */
  _onUpdateButtonStates = () => {};
  /** @type {(e: MouseEvent) => void} */
  _onUpClick = () => {};
  /** @type {(e: MouseEvent) => void} */
  _onDownClick = () => {};

  connectedCallback() {
    // Use querySelector instead of refs to avoid interfering with parent component
    const thumbnailsContainer = this.querySelector(
      ".slideshow-controls__thumbnails-container",
    );
    const thumbnailScrollUp = this.querySelector('[ref="thumbnailScrollUp"]');
    const thumbnailScrollDown = this.querySelector(
      '[ref="thumbnailScrollDown"]',
    );

    // Only set up thumbnail scroll handling if we have the necessary elements
    if (
      thumbnailsContainer instanceof HTMLElement &&
      (thumbnailScrollUp instanceof HTMLButtonElement ||
        thumbnailScrollDown instanceof HTMLButtonElement)
    ) {
      this.#setupThumbnailScrollHandling(
        thumbnailsContainer,
        thumbnailScrollUp instanceof HTMLButtonElement
          ? thumbnailScrollUp
          : undefined,
        thumbnailScrollDown instanceof HTMLButtonElement
          ? thumbnailScrollDown
          : undefined,
      );
    }
  }

  disconnectedCallback() {
    // Use querySelector instead of refs to avoid interfering with parent component
    const thumbnailsContainer = this.querySelector(
      ".slideshow-controls__thumbnails-container",
    );
    const thumbnailScrollUp = this.querySelector('[ref="thumbnailScrollUp"]');
    const thumbnailScrollDown = this.querySelector(
      '[ref="thumbnailScrollDown"]',
    );

    // Only set up thumbnail scroll handling if we have the necessary elements
    if (
      thumbnailsContainer instanceof HTMLElement &&
      (thumbnailScrollUp instanceof HTMLButtonElement ||
        thumbnailScrollDown instanceof HTMLButtonElement)
    ) {
      this.#disconnectThumbnailScrollHandling(
        thumbnailsContainer,
        thumbnailScrollUp instanceof HTMLButtonElement
          ? thumbnailScrollUp
          : undefined,
        thumbnailScrollDown instanceof HTMLButtonElement
          ? thumbnailScrollDown
          : undefined,
      );
    }
  }

  updateButtonStates(container, upBtn, downBtn) {
    const atTop = container.scrollTop <= 1;
    const atBottom =
      container.scrollTop >=
      container.scrollHeight - container.clientHeight - 1;

    if (upBtn) upBtn.disabled = atTop;
    if (downBtn) downBtn.disabled = atBottom;

    if (upBtn && downBtn) {
      if (upBtn.disabled === true && downBtn.disabled === true) {
        if (
          container &&
          container.parentElement &&
          !container.parentElement.classList.contains("hide-scroll-controls")
        ) {
          container.parentElement.classList.add("hide-scroll-controls");
        }
      } else {
        if (
          container &&
          container.parentElement &&
          container.parentElement.classList.contains("hide-scroll-controls")
        ) {
          container.parentElement.classList.remove("hide-scroll-controls");
        }
      }
    }
  }

  /**
   * Sets up scroll event handling for thumbnail navigation buttons.
   * Enables/disables buttons based on scroll position to indicate when
   * more content is available in each direction.
   *
   * @param {HTMLElement} container - The scrollable thumbnails container
   * @param {HTMLButtonElement | undefined} upBtn - The scroll up button
   * @param {HTMLButtonElement | undefined} downBtn - The scroll down button
   */
  #setupThumbnailScrollHandling(container, upBtn, downBtn) {
    // Store handler references on the instance for proper removal
    this._onUpdateButtonStates = () =>
      this.updateButtonStates(container, upBtn, downBtn);

    container.addEventListener("scroll", this._onUpdateButtonStates, {
      passive: true,
    });
    requestAnimationFrame(this._onUpdateButtonStates);

    if (this.closest(".product-information__media")) {
      window.addEventListener("resize", this._onUpdateButtonStates, {
        passive: true,
      });
    }

    if (upBtn) {
      this._onUpClick = (e) => {
        e.stopPropagation();
        container.scrollBy({ top: -200, behavior: "smooth" });
        if (this._onUpdateButtonStates)
          setTimeout(this._onUpdateButtonStates, 300);
      };
      upBtn.addEventListener("click", this._onUpClick);
    }

    if (downBtn) {
      this._onDownClick = (e) => {
        e.stopPropagation();
        container.scrollBy({ top: 200, behavior: "smooth" });
        if (this._onUpdateButtonStates)
          setTimeout(this._onUpdateButtonStates, 300);
      };
      downBtn.addEventListener("click", this._onDownClick);
    }
  }

  #disconnectThumbnailScrollHandling(container, upBtn, downBtn) {
    if (!container) return;
    if (this._onUpdateButtonStates) {
      container.removeEventListener("scroll", this._onUpdateButtonStates);

      if (this.closest(".product-information__media")) {
        window.removeEventListener("resize", this._onUpdateButtonStates);
      }
    }
    if (upBtn && this._onUpClick) {
      upBtn.removeEventListener("click", this._onUpClick);
    }
    if (downBtn && this._onDownClick) {
      downBtn.removeEventListener("click", this._onDownClick);
    }
    this._onUpdateButtonStates = () => {};
    this._onUpClick = () => {};
    this._onDownClick = () => {};
  }
}

if (!customElements.get("slideshow-controls")) {
  customElements.define("slideshow-controls", SlideshowControls);
}
